
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BookUser, MessageCircle, Briefcase, CalendarClock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface MentorProps {
  id: string;
  user_id: string;
  name: string;
  title: string;
  company: string;
  bio: string;
  experience_years: number;
  calendly_url?: string;
  avatar_url?: string;
  expertise: string[];
}

const MentorCard: React.FC<MentorProps> = ({ 
  id, name, title, company, bio, calendly_url, avatar_url, expertise 
}) => {
  const isMobile = useIsMobile();
  
  // Open Calendly in a new tab
  const handleBookSession = () => {
    if (calendly_url) {
      window.open(calendly_url, '_blank');
    }
  };
  
  return (
    <Card className="overflow-hidden transition-all duration-300 hover:-translate-y-1 card-shadow">
      <CardContent className="p-0">
        <div className={`${isMobile ? "p-4" : "p-6"}`}>
          <div className="flex items-center">
            <Avatar className={`${isMobile ? "h-10 w-10" : "h-12 w-12"} border-2 border-brand-light`}>
              <AvatarImage src={avatar_url || "/placeholder.svg"} alt={name} />
              <AvatarFallback className="bg-brand-purple text-white">
                {name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div className="ml-4">
              <h3 className={`font-semibold ${isMobile ? "text-sm" : ""}`}>{name}</h3>
              <p className={`${isMobile ? "text-xs" : "text-sm"} text-muted-foreground`}>
                {title} at {company}
              </p>
            </div>
          </div>
          
          <div className="mt-4">
            <p className={`${isMobile ? "text-xs" : "text-sm"} text-muted-foreground line-clamp-2`}>
              {bio}
            </p>
          </div>
          
          <div className="mt-3 flex flex-wrap gap-2">
            {expertise.map((specialty) => (
              <Badge key={specialty} variant="outline" className={`bg-muted/50 ${isMobile ? "text-[10px] px-1.5 py-0.5" : "text-xs"}`}>
                {specialty}
              </Badge>
            ))}
          </div>
        </div>
        
        <div className={`border-t border-border p-4 ${isMobile ? "p-3" : "p-4"} bg-muted/30 flex justify-between items-center`}>
          <Button 
            variant="outline" 
            size={isMobile ? "xs" : "sm"} 
            className="flex items-center"
            onClick={() => {}}
          >
            <MessageCircle className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} mr-2`} />
            Connect
          </Button>
          
          {calendly_url && (
            <Button 
              size={isMobile ? "xs" : "sm"} 
              className="flex items-center"
              onClick={handleBookSession}
            >
              <CalendarClock className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} mr-2`} />
              Book Session
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const MentorsSection: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mentors, setMentors] = useState<MentorProps[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch approved mentors
  useEffect(() => {
    const fetchMentors = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('mentors')
          .select(`
            *,
            profiles:profiles(id, full_name, avatar_url)
          `)
          .eq('status', 'approved');
        
        if (error) {
          console.error("Error fetching mentors:", error);
          setMentors([]);
          return;
        }
        
        const formattedMentors = data.map((mentor: any) => ({
          id: mentor.id,
          user_id: mentor.user_id,
          name: mentor.profiles?.full_name || "Anonymous Mentor",
          title: mentor.position || "",
          company: mentor.company || "",
          bio: mentor.bio || "No bio provided.",
          experience_years: mentor.experience_years || 0,
          calendly_url: mentor.calendly_url,
          avatar_url: mentor.profiles?.avatar_url,
          expertise: mentor.expertise || [],
        }));
        
        setMentors(formattedMentors);
      } catch (error) {
        console.error("Error fetching mentors:", error);
        setMentors([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMentors();
  }, []);

  const handleBecomeMentor = () => {
    navigate('/become-mentor');
  };
  
  return (
    <div className="bg-muted/30 py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Connect with Mentors</h2>
            <p className="mt-2 text-lg text-muted-foreground max-w-2xl">
              Learn from professionals who've been in your shoes and can guide your journey.
            </p>
          </div>
          <Button 
            className="mt-4 md:mt-0 flex items-center" 
            variant="outline"
            onClick={handleBecomeMentor}
          >
            <Briefcase className="h-4 w-4 mr-2" />
            Become a Mentor
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-muted animate-pulse" />
                    <div className="ml-4 space-y-2">
                      <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                      <div className="h-3 w-32 bg-muted animate-pulse rounded" />
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="h-3 w-full bg-muted animate-pulse rounded" />
                    <div className="h-3 w-3/4 bg-muted animate-pulse rounded" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : mentors.length > 0 ? (
          <div className={`grid grid-cols-1 ${isMobile ? "grid-cols-1" : "md:grid-cols-2 lg:grid-cols-3"} gap-6`}>
            {mentors.map((mentor) => (
              <MentorCard key={mentor.id} {...mentor} />
            ))}
          </div>
        ) : (
          <div className="bg-background border rounded-lg p-8 text-center">
            <BookUser className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-medium mb-2">No mentors available yet</h3>
            <p className="text-muted-foreground mb-6">
              Be the first to join our mentorship program and help students achieve their goals.
            </p>
            <Button onClick={handleBecomeMentor}>
              Become a Mentor
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MentorsSection;
